<template>
  <div class="container-fluid">
    <div class="row">
        <div class="col-12">
            <div class="card">
                <div class="card-header pb-0">
                    <div class="d-flex justify-content-between">
                        <button 
                            type="button"
                            @click="download"
                            :class="'btn btn-xs btn-outline-primary text-xs font-weight-normal m-0 ' + (loading ? 'disabled' : '')">
                            <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true" v-if="loading"></span>
                            <i class="fa fa-download" v-if="!loading"></i> Download
                        </button>
                        <argon-badge color="info" class="text-xxs align-self-center" v-if="bill">{{ bill.state }}</argon-badge>
                    </div>
                    <hr/>
                </div>
                <div class="card-body min-vh-50 pt-0" v-if="bill">
                    <h5>{{ bill.name }}</h5>
                    <div class="row">
                        <div class="col-sm-6">
                            <table class="text-sm" width="100%">
                                <tbody>
                                    <tr>
                                        <td class="text-secondary align-top" width="30%">Vendor</td>
                                        <td class="fw-bold align-top">{{ bill.partner_name }}</td>
                                    </tr>
                                    <tr>
                                        <td class="text-secondary align-top">Bill Reference</td>
                                        <td class="fw-bold align-top">{{ bill.ref }}</td>
                                    </tr>
                                    <tr>
                                        <td class="text-secondary align-top">Recipient Bank</td>
                                        <td class="fw-bold align-top">{{ bill.currency_name }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div class="col-sm-6">
                            <table class="text-sm" width="100%">
                                <tbody>
                                    <tr>
                                        <td class="text-secondary align-top" width="30%">Vendor Invoice Date</td>
                                        <td class="fw-bold align-top">{{ bill.vendor_invoice_date }}</td>
                                    </tr>
                                    <tr>
                                        <td class="text-secondary align-top" width="30%">Vendor Invoice Due Date</td>
                                        <td class="fw-bold align-top">{{ bill.vendor_invoice_due_date }}</td>
                                    </tr>
                                    <tr>
                                        <td class="text-secondary align-top">Invoice Received Date</td>
                                        <td class="fw-bold align-top">{{ bill.invoice_date }}</td>
                                    </tr>
                                    <tr>
                                        <td class="text-secondary align-top">Invoice Due Date</td>
                                        <td class="fw-bold align-top">{{ bill.invoice_date_due }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div class="table-responsive p-0 mt-3">
                        <table class="table align-items-center mb-0">
                            <thead>
                                <tr class="bg-light">
                                    <th class="text-uppercase text-xs fw-bolder opacity-7 ps-2">#</th>
                                    <th class="text-uppercase text-xs fw-bolder opacity-7 ps-2">Product</th>
                                    <th class="text-uppercase text-xs fw-bolder opacity-7 ps-2">Description</th>
                                    <th class="text-uppercase text-xs fw-bolder opacity-7 ps-2">Quantity</th>
                                    <th class="text-uppercase text-xs fw-bolder opacity-7 ps-2">Unit Price (Exclude VAT)</th>
                                    <th class="text-uppercase text-xs fw-bolder opacity-7 ps-2" v-if="bill.vendor_tax_identity_pkp">Taxes</th>
                                    <th class="text-uppercase text-xs fw-bolder opacity-7 ps-2">Subtotal</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="line,i in bill_lines" :key="line.move_line_id">
                                    <td class="text-sm mb-0 ps-2">{{ i+1 }}</td>
                                    <td>
                                        <p class="text-sm mb-0">{{ line.name }}</p>
                                    </td>
                                    <td>
                                        <p class="text-sm mb-0">{{ line.name }}</p>
                                    </td>
                                    <td>
                                        <p class="text-sm text-end mb-0">{{ line.quantity }}</p>
                                    </td>
                                    <td>
                                        <p class="text-sm text-end mb-0">{{ line.price_unit }}</p>
                                    </td>
                                    <td v-if="bill.vendor_tax_identity_pkp">
                                        <div class="d-grid gap-2 d-flex">
                                            <argon-badge color="info" class="text-xxs align-self-center" v-for="tax,i in line.account_tax_ids" :key="i">{{ tax }}</argon-badge>
                                        </div>
                                    </td>
                                    <td>
                                        <p class="text-sm text-end mb-0 px-2">{{ line.price_subtotal }}</p>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="row mt-3">
                        <div class="col-md offset-md-8 px-4">
                            <table class="text-sm fw-bold" width="100%">
                                <tbody>
                                    <template v-if="bill.vendor_tax_identity_pkp">
                                        <tr>
                                            <td>Untaxed Amount</td>
                                            <td class="fw-bold text-end">{{ bill.amount_untaxed }}</td>
                                        </tr>
                                        <tr>
                                            <td>Taxes</td>
                                            <td class="fw-bold text-end">{{ bill.amount_tax }}</td>
                                        </tr>
                                    </template>
                                    <tr>
                                        <td class="border-top">Amount Total</td>
                                        <td class="border-top fw-bold text-end">{{ bill.amount_total }}</td>
                                    </tr>
                                    <tr>
                                        <td>Amount Due</td>
                                        <td class="fw-bold text-end">{{ bill.amount_residual }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div class="row mt-3 text-sm" v-if="bill_payments.length > 0">
                        <div class="col-md-6">
                            <p class="mb-0 fw-bold">Payment Details</p>
                            <table width="100%">
                                <thead>
                                    <tr>
                                        <th>Voucher No.</th>
                                        <th>Date</th>
                                        <th>Amount</th>
                                        <th>Memo/Notes</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="payment,i in bill_payments" :key="i">
                                        <td>{{ payment.name }}</td>
                                        <td>{{ payment.payment_date }}</td>
                                        <td>{{ payment.amount }}</td>
                                        <td>{{ payment.communication }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
  </div>
</template>

<script>
import ArgonBadge from '../components/ArgonBadge.vue'
const endpoint = require("../router/endpoint");

export default {
  name: "vendor-bill-detail",
  data() {
    return {
      bill: null,
      loading: false,
      bill_lines: [],
      bill_payments: []
    };
  },
  components: {
    ArgonBadge
  },
  methods: {
    getBillDetail() {
      this.loading = true
      this.axios
        .post(
          endpoint.BILL_LIST,
          {
            partner_id: parseInt(localStorage.getItem("partner_id")),
            bills_id: parseInt(this.$route.params.id.split('_')[0]),
            limit: 1,
            offset: 0
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`
            }
          }
        )
        .then((response) => {
          if (response.data.msg_status === "0002"){
              this.$notify({
                type: 'warn',
                title: 'Expired',
                text: 'Session expired, please sign in again.',
              });
              this.$router.replace('/signin')
              return false
          } else if (response.data.response_code === "0000") {
            this.bill = response.data.data[0];
            this.loading = false;
          } else {
            this.$notify({
                type: 'warn',
                title: 'Warning',
                text: response.data.message,
            });
            this.loading = false;
          }
        })
        .catch((e) => {
          this.$notify({
              type: 'error',
              title: 'Something went wrong!',
              text: e.response.data.message || e,
          });
          this.loading = false;
          this.$router.replace('/vendor-bill')
        });
    },
    getBillLines() {
      this.loading = true
      this.axios
        .post(
        endpoint.BILL_LINES,
        {
            partner_id: parseInt(localStorage.getItem("partner_id")),
            move_id: parseInt(this.$route.params.id.split('_')[0]),
            limit: 1000,
            offset: 0
        },
        {
            headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`
            }
        }
        )
        .then((response) => {
        if (response.data.msg_status === "0002"){
              this.$notify({
                type: 'warn',
                title: 'Expired',
                text: 'Session expired, please sign in again.',
              });
              this.$router.replace('/signin')
              return false
        } else if (response.data.response_code === "0000") {
            this.bill_lines = response.data.data;
        } else {
            this.$notify({
                type: 'warn',
                title: 'Warning',
                text: response.data.message,
            });
            this.loading = false;
        }
        })
        .catch((e) => {
        this.loading = false;
        this.$notify({
            type: 'error',
            title: 'Something went wrong!',
            text: e.response.data.message || e,
            });
        });
    },
    getBillPayments() {
      this.loading = true
      this.axios
        .post(
        endpoint.BILL_PAYMENT,
        {
            partner_id: parseInt(localStorage.getItem("partner_id")),
            move_id: parseInt(this.$route.params.id.split('_')[0]),
            limit: 1000,
            offset: 0
        },
        {
            headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`
            }
        }
        )
        .then((response) => {
        if (response.data.msg_status === "0002"){
            this.$notify({
                type: 'warn',
                title: 'Expired',
                text: 'Session expired, please sign in again.',
            });
            this.$router.replace('/signin')
            return false
        } else if (response.data.response_code === "0000") {
            this.bill_payments = response.data.data;
        } else {
            if (response.data.response_code !== "4000"){
            this.$notify({
                type: 'warn',
                title: 'Warning',
                text: response.data.message,
            });
        }
            this.loading = false;
        }
        })
        .catch((e) => {
        this.loading = false;
        this.$notify({
            type: 'error',
            title: 'Something went wrong!',
            text: e.response.data.message || e,
            });
        });
    },
    download () {
      this.loading = true
      this.axios
          .post(
          endpoint.DOWNLOAD,
          {
            partner_id: parseInt(localStorage.getItem("partner_id")),
            res_id: parseInt(this.$route.params.id.split('_')[0]),
            template_report: 'account.account_invoices'
          },
          {
              headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`
              }
          }
          )
          .then((response) => {
          if (response.data.msg_status === "0002"){
              this.$notify({
                type: 'warn',
                title: 'Expired',
                text: 'Session expired, please sign in again.',
              });
              this.$router.replace('/signin')
              return false
          } else if (response.data.response_code === "0000") {
              const linkSource = `data:application/pdf;base64,${response.data.data.file}`;
              const downloadLink = document.createElement("a");
              const fileName = this.bill.name + ".pdf";
              downloadLink.href = linkSource;
              downloadLink.download = fileName;
              downloadLink.click();
          } else {
              if (response.data.response_code !== "4000"){
              this.$notify({
                  type: 'warn',
                  title: 'Warning',
                  text: response.data.message,
              });
              }
          }
          this.loading = false
          })
          .catch((e) => {
            this.loading = false;
            this.$notify({
                type: 'error',
                title: 'Something went wrong!',
                text: e.response.data.message || e,
                });
          });
  }
  },
  mounted() {
    this.getBillDetail();
    this.getBillLines();
    this.getBillPayments();
  }
};
</script>

<style>

</style>